import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

type ThemeName = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly storageKey = 'app-theme';
  private readonly defaultTheme: ThemeName = 'light';
  private readonly themeSubject = new BehaviorSubject<ThemeName>(this.defaultTheme);

  readonly themeChanges: Observable<ThemeName> = this.themeSubject.asObservable();

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    const initial = this.getStoredTheme() ?? this.getPreferredScheme() ?? this.defaultTheme;
    this.applyTheme(initial, false);
    this.bindSystemSchemeListener();
  }

  get current(): ThemeName {
    return this.themeSubject.value;
  }

  setTheme(theme: ThemeName): void {
    this.applyTheme(theme, true);
  }

  toggleTheme(): void {
    const next: ThemeName = this.current === 'light' ? 'dark' : 'light';
    this.applyTheme(next, true);
  }

  private applyTheme(theme: ThemeName, persist: boolean): void {
    this.document.documentElement.setAttribute('data-theme', theme);
    this.themeSubject.next(theme);
    if (persist) {
      try { localStorage.setItem(this.storageKey, theme); } catch { /* noop */ }
    }
  }

  private getStoredTheme(): ThemeName | null {
    try {
      const t = localStorage.getItem(this.storageKey) as ThemeName | null;
      return t === 'light' || t === 'dark' ? t : null;
    } catch {
      return null;
    }
  }

  private getPreferredScheme(): ThemeName | null {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private bindSystemSchemeListener(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't explicitly chosen a theme.
      const stored = this.getStoredTheme();
      if (stored) return;
      this.applyTheme(e.matches ? 'dark' : 'light', false);
    };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
    } else {
      // Safari
      // @ts-ignore
      mq.addEventListener(handler);
    }
  }
}
